import React, { useState, useRef } from "react";
import {
  Grid,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import SettingsIcon from "@material-ui/icons/Settings";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { Alert } from "@material-ui/lab";
import StyledActionButton from "./StyledActionButton";
import ResponseDisplay from "./ResponseDisplay";
import PostIngestionWarning from "./PostIngestionWarning";
import { useCustomerCreation, useCustomerOptions } from "../hooks";
import { COLORS } from "../styles/theme";
import { PREDEFINED_ADDRESSES } from "../config/addresses";

interface CustomerCreationSectionProps {
  onCustomerCreated?: (response: any) => void;
  patientDetails?: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    gender?: string;
    phoneNumber?: string;
    zipCode?: string;
  };
  showAcknowledgment?: boolean;
  showExistingCustomerWarning?: boolean;
  showCriticalPostIngestionWarning?: boolean;
  stepNumber?: string | number;
  title?: string;
  subtitle?: string;
}

const CustomerCreationSection: React.FC<CustomerCreationSectionProps> = ({
  onCustomerCreated,
  patientDetails,
  showAcknowledgment = false,
  showExistingCustomerWarning = false,
  showCriticalPostIngestionWarning = false,
  stepNumber = "3",
  title = "Create Pharmacy Customer (Optional)",
  subtitle = "Create a new pharmacy customer using the prescription data",
}) => {
  const [emailPrefix, setEmailPrefix] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [acknowledgeNotEnrolled, setAcknowledgeNotEnrolled] = useState(false);
  const [customerCreated, setCustomerCreated] = useState(false);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState<number>(0);

  const customerResponseRef = useRef<HTMLDivElement>(null);

  const {
    isEnrolling,
    enrollmentResponse,
    error: customerError,
    createCustomer,
    getAddressIdForZipCode,
  } = useCustomerCreation();

  const {
    options,
    handlePrimeChange,
    handleRxPassSubscriptionChange,
    handleRxPassForceActiveChange,
    handleHornbillSubscriptionChange,
    handleSetDefaultAddressChange,
  } = useCustomerOptions();

  const handleCreateCustomer = async () => {
    try {
      let residentialAddressId: string | undefined;

      // Determine which zipCode to use for address lookup
      let zipCodeToUse = patientDetails?.zipCode;

      // If user selected an address from dropdown, use that zipCode
      if (
        selectedAddressIndex >= 0 &&
        selectedAddressIndex < PREDEFINED_ADDRESSES.length
      ) {
        zipCodeToUse = PREDEFINED_ADDRESSES[selectedAddressIndex].zipCode;
      }

      // Get address ID for the selected zipCode
      if (zipCodeToUse) {
        const addressId = await getAddressIdForZipCode(zipCodeToUse);
        residentialAddressId = addressId || undefined;
      }

      // Convert gender from "M"/"F" to "Male"/"Female" if needed
      let genderFull: string | undefined;
      if (patientDetails?.gender) {
        genderFull =
          patientDetails.gender === "M"
            ? "Male"
            : patientDetails.gender === "F"
            ? "Female"
            : patientDetails.gender;
      }

      const response = await createCustomer({
        firstName: patientDetails?.firstName,
        lastName: patientDetails?.lastName,
        dateOfBirth: patientDetails?.dateOfBirth,
        gender: genderFull,
        emailPrefix: emailPrefix.trim() || undefined,
        phoneNumber:
          patientDetails?.phoneNumber?.trim() ||
          phoneNumber.trim() ||
          undefined,
        residentialAddressId,
        options,
      });

      setCustomerCreated(true);

      // Call the callback if provided
      if (onCustomerCreated) {
        onCustomerCreated(response);
      }

      // Scroll to customer response
      setTimeout(() => {
        if (customerResponseRef.current) {
          customerResponseRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    } catch (err) {
      console.error("Error creating customer:", err);
    }
  };

  const isFormDisabled = showAcknowledgment && !acknowledgeNotEnrolled;

  return (
    <>
      {!customerCreated && (
        <Grid item xs={12} style={{ maxWidth: 1200, width: "100%" }}>
          <Paper
            elevation={2}
            style={{
              width: "100%",
              marginTop: 32,
              padding: 24,
              borderRadius: 8,
              backgroundColor: "#f5f5f5",
              border: "1px solid #e0e0e0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  backgroundColor: "#546e7a",
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 16,
                }}
              >
                <Typography
                  style={{
                    color: COLORS.white,
                    fontSize: 20,
                    fontWeight: "bold",
                  }}
                >
                  {stepNumber}
                </Typography>
              </div>
              <div>
                <Typography
                  variant="h6"
                  style={{
                    color: COLORS.textPrimary,
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  {title}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {subtitle}
                </Typography>
              </div>
            </div>

            <Grid container spacing={3}>
              {showExistingCustomerWarning && (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography
                        variant="body2"
                        style={{ fontWeight: "bold", marginBottom: 4 }}
                      >
                        Important: Linking Rx to Existing AP Customer
                      </Typography>
                      <Typography variant="body2">
                        If the prescription belongs to an existing Amazon
                        Pharmacy customer, ensure the patient details in the S3
                        file <strong>exactly match</strong> the customer's
                        profile (name, date of birth, address, etc.). This
                        ensures the prescription will be properly linked to
                        their account.{" "}
                        <strong>Do NOT create a new customer</strong> if one
                        already exists.
                      </Typography>
                    </Alert>
                  </Grid>
                </>
              )}

              {showCriticalPostIngestionWarning && <PostIngestionWarning />}

              {showAcknowledgment && (
                <>
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      <Typography
                        variant="body2"
                        style={{ fontWeight: "bold" }}
                      >
                        Important: Only create a customer if they are NOT
                        already enrolled in the system
                      </Typography>
                    </Alert>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={acknowledgeNotEnrolled}
                          onChange={(e) =>
                            setAcknowledgeNotEnrolled(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={
                        showCriticalPostIngestionWarning
                          ? "I acknowledge the risks and confirm this customer is NOT already enrolled in the system"
                          : "I acknowledge that this customer is NOT already enrolled in the system"
                      }
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email Prefix (Optional)"
                      variant="outlined"
                      value={emailPrefix}
                      onChange={(e) => setEmailPrefix(e.target.value)}
                      placeholder="e.g., john.doe"
                      helperText="Leave empty to auto-generate"
                      fullWidth
                      disabled={isFormDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number (Optional)"
                      variant="outlined"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="e.g., 1234567890"
                      helperText="Leave empty to auto-generate"
                      fullWidth
                      disabled={isFormDisabled}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  disabled={isFormDisabled}
                >
                  <InputLabel>Select Address</InputLabel>
                  <Select
                    value={selectedAddressIndex}
                    onChange={(e) =>
                      setSelectedAddressIndex(e.target.value as number)
                    }
                    label="Select Address"
                  >
                    {PREDEFINED_ADDRESSES.map((address, index) => (
                      <MenuItem key={index} value={index}>
                        {address.addressLine1}
                        {address.addressLine2 && `, ${address.addressLine2}`}
                        {`, ${address.city}, ${address.stateName}, ${address.zipCode}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ marginTop: 4, display: "block" }}
                >
                  Select a predefined address for the customer
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontWeight: 600,
                  }}
                >
                  <SettingsIcon style={{ color: "#546e7a", fontSize: 20 }} />
                  Customer Options
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.prime}
                          onChange={(e) => handlePrimeChange(e.target.checked)}
                          color="primary"
                          disabled={isFormDisabled}
                        />
                      }
                      label="Prime Member"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.rxPassSubscription}
                          onChange={(e) =>
                            handleRxPassSubscriptionChange(e.target.checked)
                          }
                          color="primary"
                          disabled={isFormDisabled}
                        />
                      }
                      label="RxPass Subscription (requires Prime)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.rxPassForceActive}
                          onChange={(e) =>
                            handleRxPassForceActiveChange(e.target.checked)
                          }
                          color="primary"
                          disabled={isFormDisabled}
                        />
                      }
                      label="RxPass Force Active (requires Prime + RxPass)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.hornbillSubscription}
                          onChange={(e) =>
                            handleHornbillSubscriptionChange(e.target.checked)
                          }
                          color="primary"
                          disabled={isFormDisabled}
                        />
                      }
                      label="Hornbill Subscription"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.setDefaultAddress}
                          onChange={(e) =>
                            handleSetDefaultAddressChange(e.target.checked)
                          }
                          color="primary"
                          disabled={isFormDisabled}
                        />
                      }
                      label="Set Default Address"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <StyledActionButton
                  label="Create Pharmacy Customer"
                  loadingLabel="Creating Customer..."
                  onClick={handleCreateCustomer}
                  disabled={isFormDisabled}
                  loading={isEnrolling}
                  color={COLORS.orange}
                />
              </Grid>

              {customerError && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>Error:</strong> {customerError}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      )}

      {enrollmentResponse && (
        <ResponseDisplay
          title="Customer Creation Response"
          subtitle={`PersonId: ${
            enrollmentResponse.profile?.[0]?.personId || "N/A"
          }`}
          data={enrollmentResponse}
          color={COLORS.iconGray}
          icon={<CheckCircleIcon />}
          forwardRef={customerResponseRef}
        />
      )}
    </>
  );
};

export default CustomerCreationSection;
